import React, { useMemo, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, TextField, Button, Stack } from '@mui/material';

export interface CommentEntry {
  id: string;
  author?: string;
  createdAt?: string | Date;
  text: string;
  context?: string; // e.g., "Wind Up", "Compliance", etc.
}

interface CommentsLogProps {
  title?: string;
  entries: CommentEntry[];
  onAdd?: (text: string) => Promise<void> | void;
  placeholder?: string;
}

const CommentsLog: React.FC<CommentsLogProps> = ({ title = 'Comments', entries, onAdd, placeholder }) => {
  const [text, setText] = useState('');
  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bt - at;
    });
  }, [entries]);

  const handleAdd = async () => {
    if (!onAdd || !text.trim()) return;
    await onAdd(text.trim());
    setText('');
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>{title}</Typography>
      <List dense>
        {sorted.length === 0 ? (
          <Box color="text.secondary" sx={{ p: 1 }}>No comments yet.</Box>
        ) : (
          sorted.map((e) => (
            <React.Fragment key={e.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={e.text}
                  secondary={
                    <>
                      <span>{e.author || 'System'}</span>
                      {e.createdAt ? ` • ${new Date(e.createdAt).toLocaleString()}` : ''}
                      {e.context ? ` • ${e.context}` : ''}
                    </>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))
        )}
      </List>
      {onAdd && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={text}
            placeholder={placeholder || 'Add a comment'}
            onChange={(e) => setText(e.target.value)}
          />
          <Button variant="contained" onClick={handleAdd} disabled={!text.trim()}>Add</Button>
        </Stack>
      )}
    </Box>
  );
};

export default CommentsLog;


